import React, { useState } from "react";
import { GitBranch, GitMerge, Search, X } from "lucide-react";
import { BranchEntry } from "../../../api/v2/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import BranchSelector from "./BranchSelector";
import InlineHint from "../../shared/InlineHint";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import EditorQuickFilters from "./EditorQuickFilters";
import { EditorQuickFiltersState } from "../utils/editorPresentationUtils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";

interface EditorToolbarProps {
  group: string;
  groups: string[];
  onGroupChange: (value: string) => void;
  branch: string;
  branches: BranchEntry[];
  defaultBranch?: string;
  workingBranch?: string | null;
  onBranchChange: (value: string) => void;
  onCreateBranch: (value: string) => Promise<void>;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  quickFilters: EditorQuickFiltersState;
  onQuickFiltersChange: (next: EditorQuickFiltersState) => void;
  onResetFilters: () => void;
  onPreviewMerge: () => void;
  isPreviewingMerge?: boolean;
  isDisabled?: boolean;
}

const EditorToolbar = ({
  group,
  groups,
  onGroupChange,
  branch,
  branches,
  defaultBranch,
  workingBranch,
  onBranchChange,
  onCreateBranch,
  searchQuery,
  onSearchQueryChange,
  quickFilters,
  onQuickFiltersChange,
  onResetFilters,
  onPreviewMerge,
  isPreviewingMerge = false,
  isDisabled = false,
}: EditorToolbarProps) => {
  const [newBranchName, setNewBranchName] = useState("");

  return (
    <div className="sticky top-2 z-10 space-y-3 rounded border bg-background/95 p-3 backdrop-blur">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm">Group</span>
          <InlineHint text="Group filter limits editor data to selected translation scope." />
          <Select
            value={group || "__all__"}
            onValueChange={(value) => onGroupChange(value === "__all__" ? "" : value)}
            disabled={isDisabled}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Group filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All groups</SelectItem>
              {groups.map((item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4" />
          <span className="text-sm">Branch</span>
          <InlineHint text="Default branch is the PR target. Working branch is used for save commits. Switch branch to choose active editing scope." />
          <BranchSelector
            branches={branches}
            value={branch}
            defaultBranch={defaultBranch}
            workingBranch={workingBranch}
            onChange={onBranchChange}
            disabled={isDisabled}
          />
          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" disabled={isDisabled}>
                <GitBranch className="mr-1 h-4 w-4" /> New branch
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create branch and activate it</DialogTitle>
                <DialogDescription>
                  Enter branch name to create and set as working branch. If branch already exists, it will be reused.
                </DialogDescription>
              </DialogHeader>
              <Input
                value={newBranchName}
                onChange={(event) => setNewBranchName(event.target.value)}
                placeholder="feature/localization-update"
              />
              <DialogFooter>
                <Button
                  disabled={isDisabled || !newBranchName.trim()}
                  onClick={async () => {
                    await onCreateBranch(newBranchName.trim());
                    setNewBranchName("");
                  }}
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full min-w-[220px] md:max-w-sm">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            placeholder="Search keys and translations"
            className="pl-8"
            disabled={isDisabled}
          />
        </div>
        <EditorQuickFilters value={quickFilters} onChange={onQuickFiltersChange} />
        <Button size="sm" variant="outline" onClick={onResetFilters} disabled={isDisabled}>
          <X className="mr-1 h-4 w-4" /> Reset
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onPreviewMerge}
          disabled={isDisabled || isPreviewingMerge}
        >
          <GitMerge className="mr-1 h-4 w-4" /> Preview merge
        </Button>
      </div>
    </div>
  );
};

export default EditorToolbar;
